import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { Notification } from 'libs/mona/api/src/lib/model';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { isNullOrUndefined } from '@mona/shared/utils';
import { makeAsyncActionReducer, makeEntitiesReducer } from '@mona/store';
import { NotificationsState } from '../../entities';
import { NotificationsAction } from '../actions/notifications.actions';

export const notificationsFeatureKey = 'notifications';

/**
 * Entity adapter for notifications
 */
export const notificationsAdapter: EntityAdapter<Notification.Model> = createEntityAdapter<Notification.Model>({
    selectId: (notification: Notification.Model) => notification.id,
    sortComparer: (a: Notification.Model, b: Notification.Model) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
});

/**
 * Initial paginated notifications state
 */
const initialPaginatedNotificationsState: NotificationsState = {
    allNotifications: notificationsAdapter.getInitialState(),
    unreadNotifications: notificationsAdapter.getInitialState(),
    nextUrlAll: null,
    nextUrlUnread: null,
    unreadCount: 0,
    isLoading: false,
    hasUnreadNotifications: false,
};

/**
 * Notifications reducer map
 */
export const reducer: ActionReducerMap<NotificationsState> = createReducer(
    initialPaginatedNotificationsState,
    on(NotificationsAction.loadPageableNotificationsAction, (state, action) => ({
        ...state,
        isLoading: true,
    })),
    on(NotificationsAction.loadPageableNotificationsActionSuccess, (state, action) => {
        const { payload, readStatus, isLoadMore } = action;
        const response = payload;
        const newNotifications = response.results || [];

        let nextUrlAll = state.nextUrlAll;
        let nextUrlUnread = state.nextUrlUnread;
        let allNotifications = state.allNotifications;
        let unreadNotifications = state.unreadNotifications;

        if (readStatus === false) {
            // Handle unread notifications
            nextUrlUnread = response.next;
            unreadNotifications = isLoadMore
                ? notificationsAdapter.addMany(newNotifications, state.unreadNotifications)
                : notificationsAdapter.setAll(newNotifications, state.unreadNotifications);
        } else {
            // Handle all notifications (readStatus is undefined or true)
            nextUrlAll = response.next;
            allNotifications = isLoadMore
                ? notificationsAdapter.addMany(newNotifications, state.allNotifications)
                : notificationsAdapter.setAll(newNotifications, state.allNotifications);
        }

        return {
            ...state,
            allNotifications,
            unreadNotifications,
            nextUrlAll,
            nextUrlUnread,
            unreadCount: response.unreadCount || 0,
            isLoading: false,
        };
    }),
    on(NotificationsAction.loadPageableNotificationsActionFailed, state => ({
        ...state,
        isLoading: false,
    })),
    on(NotificationsAction.loadPageableNotificationsActionCancelled, state => ({
        ...state,
        isLoading: false,
    })),
    on(NotificationsAction.clearPaginatedNotificationsAction, () => initialPaginatedNotificationsState),
    on(EncountersActions.clearEncounter, () => initialPaginatedNotificationsState),
    on(NotificationsAction.markMultipleNotificationsAsReadActionSuccess, (state, action) => {
        const updates = action.notifications.map(notification => ({
            id: notification.id,
            changes: { readStatus: true },
        }));
        return {
            ...state,
            allNotifications: notificationsAdapter.updateMany(updates, state.allNotifications),
            unreadCount: state.unreadCount - updates.length,
        };
    }),
    on(NotificationsAction.loadHasUnreadNotificationsAction.succeededAction, (state, action) => ({
        ...state,
        hasUnreadNotifications: action.payload.hasUnread,
    })),
    on(NotificationsAction.loadHasUnreadNotificationsAction.clearAction, state => ({
        ...state,
        hasUnreadNotifications: false,
    })),
    on(NotificationsAction.addUpdateNotificationAction, (state, action) => {
        const unreadCount = action.notification.readStatus ? state.unreadCount - 1 : state.unreadCount + 1;

        const unreadNotifications = action.notification.readStatus
            ? notificationsAdapter.removeOne(action.notification.id, state.unreadNotifications)
            : notificationsAdapter.upsertOne(action.notification, state.unreadNotifications);
        return {
            ...state,
            allNotifications: notificationsAdapter.upsertOne(action.notification, state.allNotifications),
            unreadNotifications,
            unreadCount,
        };
    }),
    on(NotificationsAction.markMultipleNotificationsAsReadActionSuccess, (state, action) => {
        const updates = action.notifications.map(notification => ({
            id: notification.id,
            changes: { readStatus: true },
        }));
        return {
            ...state,
            allNotifications: notificationsAdapter.updateMany(updates, state.allNotifications),
            unreadNotifications: notificationsAdapter.removeMany(
                updates.map(update => update.id),
                state.unreadNotifications,
            ),
        };
    }),
) as any;
